import { INTERACTION_QUERY_KEYS } from "../FpPages/RMS/Data/interactions";
import React, { Suspense, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Tooltip } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";

import PdfReader from "@/components/PdfReader";
import EmailViewer from "@/components/EmailViewer";
import HtmlViewer from "@/components/HtmlViewer";
import MarkdownViewer from "@/components/MarkdownViewer";
import OneNotePageViewer from "@/components/OneNoteViewer/OneNotePageViewer";
import EvestmentPageViewer from "@/components/EvestmentViewer/EvestmentPageViewer";
import TeamsPostViewer from "@/components/TeamsPostViewer/TeamsPostViewer";
import AltviaInteractionViewer from "@/components/AltviaInteractionViewer";
import FPNoteViewer from "@/components/FPNoteViewer";
import CircularProgress from "@mui/material/CircularProgress";
import useFileLoader from "./services/useFileLoader";
import LockedDocumentPanel from "./LockedDocumentPanel";
import { UnrecognizedKind, VIEWER_REGISTRY } from "./viewerRegistry";
import type { ViewerKind } from "./services/fileViewerInterface";
import {
  useViewerInteraction,
  interactionViewerKind,
} from "./services/useInteractionByBlobId";
import { RootState } from "@/redux/store";
import DocxViewer from "../common/DocxViewer";
import PptxViewer from "../common/PptxViewer";
import CitationLinkInterceptor from "../common/CitationLinkInterceptor";
import { ResponseWrapper } from "./services/useFileLoader";
import { isPreviewableUnsupportedExtension } from "../common/FileTree/fileTypeExtensions";
import { updateInteraction } from "../FpPages/RMS/Dashboard/api";
import type { InteractionResponse } from "../FpPages/RMS/Dashboard/types";
import {
  ChevronDown,
  ExternalLink,
  Paperclip,
  FileText,
  Plus,
  X,
} from "lucide-react";
import EntityIcon from "../FpPages/RMS/Dashboard/Components/EntityIcon";
import openFile from "../FpAppV4/services/openFile";
import { FileSelectionModal as V2FileSelectionModal } from "../common/FileSelectionModal";
import { formatTemporalWithTime, type Temporal } from "@/utils/temporal";

const LazyFortuneSheetExcelViewer = React.lazy(
  // fortune sheet is not properly bundled, so we need to lazy load it
  () => import("@/components/ExcelViewer/FortuneSheetExcelViewer")
);

// When the interaction happened, read under its own granularity and its own
// zone (#11055). `new Date(...)` read it in whatever zone this reader's machine
// is in and printed a time even for an occurrence that never had one -- a day
// the source stated, whose stored noon is a carrier rather than information.
const formatBannerDate = (occurrence: Temporal) =>
  formatTemporalWithTime(occurrence, "weekdayMedium");

const InteractionBanner = ({
  interaction,
}: {
  interaction: InteractionResponse;
}) => {
  const queryClient = useQueryClient();
  const [localDocumentIds, setLocalDocumentIds] = useState<string[]>(
    interaction.document_ids || []
  );
  const [showDocumentSelectionModal, setShowDocumentSelectionModal] =
    useState(false);
  // The summary line (who, when, what kind) with the people and the
  // attachments open under it, compact (Gerry, Oct 5: the block "is a bit
  // too big ... not formatted well"), and closable (Gerry, Oct 5: the
  // details "should be open by default, can optionally close them").
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    setLocalDocumentIds(interaction.document_ids || []);
  }, [interaction.document_ids]);

  const handleDocumentIdsChange = async (document_ids: string[]) => {
    if (!interaction?.id) return;
    try {
      await updateInteraction(interaction.id, { document_ids });
      setLocalDocumentIds(document_ids);
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ["interactionByBlob", interaction.document_id],
        }),
        queryClient.invalidateQueries({
          queryKey: INTERACTION_QUERY_KEYS.detail(interaction.id),
        }),
      ]);
    } catch (error) {
      console.error("Failed to update document associations:", error);
    }
  };

  const handleDocumentsSelected = async (fileIds: string[]) => {
    const mergedDocumentIds = [...new Set([...localDocumentIds, ...fileIds])];
    await handleDocumentIdsChange(mergedDocumentIds);
  };

  const participants = interaction.primary_participants ?? [];
  const mentions = interaction.mentioned_entities ?? [];
  const summary = [
    interaction.author.join(", "),
    formatBannerDate(interaction.occurrence),
    interaction.meeting_type,
  ].filter(Boolean);
  const counts = [
    participants.length > 0
      ? `${participants.length} participant${participants.length === 1 ? "" : "s"}`
      : null,
    localDocumentIds.length > 0
      ? `${localDocumentIds.length} attachment${localDocumentIds.length === 1 ? "" : "s"}`
      : null,
  ].filter(Boolean);

  return (
    <div
      data-interaction-banner=""
      className="w-full border-b border-[var(--fp-border)] bg-white text-[12.5px]"
    >
      <div className="flex w-full items-center hover:bg-[var(--fp-hover)]">
        <button
          type="button"
          aria-expanded={expanded}
          onClick={() => setExpanded((open) => !open)}
          className="flex min-w-0 flex-1 items-center gap-2 py-2 pl-4 pr-2 text-left"
        >
          <span className="min-w-0 flex-1 truncate text-[#4F5663]">
            {summary.map((part, index) => (
              <React.Fragment key={index}>
                {index > 0 && <span className="px-1.5 text-[#B0B5BF]">·</span>}
                <span className={index === 0 ? "text-[#10141B]" : undefined}>
                  {part}
                </span>
              </React.Fragment>
            ))}
          </span>
          {counts.length > 0 && (
            <span className="flex-none text-[#6E7480]">
              {counts.join(" · ")}
            </span>
          )}
          <ChevronDown
            className={`h-3.5 w-3.5 flex-none text-[#6E7480] ${
              expanded ? "rotate-180" : ""
            }`}
          />
        </button>
        {/* The record's own page, in a new tab: the banner is a summary of
          it, and the full interaction (notes, transcript, every field)
          lives there. */}
        <Tooltip title="Open interaction" placement="top" describeChild>
          <a
            href={`/rms/interaction/${interaction.id}`}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Open interaction"
            className="mr-3 flex-none rounded p-1 text-[#6E7480] hover:bg-[#ECEDEF] hover:text-[#10141B]"
          >
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </Tooltip>
      </div>

      {expanded && (
        <div className="grid grid-cols-[92px_minmax(0,1fr)] gap-x-3 gap-y-2.5 px-4 pb-3 pt-1">
          {participants.length > 0 && (
            <>
              <div className="pt-0.5 text-[#6E7480]">Participants</div>
              <div className="flex flex-wrap gap-1.5">
                {participants.map((participant, index: number) => (
                  <span
                    key={index}
                    className="inline-flex items-center gap-1 rounded-full bg-[#F7F8FF] px-2 py-0.5 text-xs text-[#2A3DCB]"
                  >
                    <EntityIcon kind={participant.kind} size={12} />
                    {participant.name}
                  </span>
                ))}
              </div>
            </>
          )}
          {mentions.length > 0 && (
            <>
              <div className="pt-0.5 text-[#6E7480]">Mentions</div>
              <div className="flex flex-wrap gap-1.5">
                {mentions.map((entity, index: number) => (
                  <span
                    key={index}
                    className="inline-flex items-center gap-1 rounded-full bg-[#ECEDEF] px-2 py-0.5 text-xs text-[#10141B]"
                  >
                    <EntityIcon kind={entity.kind} size={12} />
                    {entity.name}
                  </span>
                ))}
              </div>
            </>
          )}
          <div className="pt-1 text-[#6E7480]">Attachments</div>
          <div className="min-w-0">
            {localDocumentIds.map((docId: string) => {
              const filename = interaction.document_filenames?.[docId] || docId;
              return (
                <div
                  key={docId}
                  className="group -mx-1.5 flex items-center gap-1.5 rounded-[6px] px-1.5 py-1 hover:bg-[var(--fp-hover)]"
                >
                  <button
                    type="button"
                    onClick={() => {
                      openFile({
                        fileId: docId,
                        fileName: filename,
                        fileType: filename.split(".").pop()?.toLowerCase(),
                      });
                    }}
                    className="flex min-w-0 flex-1 items-center gap-1.5 text-left"
                  >
                    <FileText className="h-3.5 w-3.5 flex-none text-[#6E7480]" />
                    <span className="truncate text-[#10141B]">{filename}</span>
                  </button>
                  <Tooltip title="Remove" placement="top" describeChild>
                    <button
                      type="button"
                      aria-label={`Remove ${filename}`}
                      onClick={() =>
                        handleDocumentIdsChange(
                          localDocumentIds.filter((id) => id !== docId)
                        )
                      }
                      className="flex-none rounded p-0.5 text-[#6E7480] opacity-0 hover:text-[#10141B] focus-visible:opacity-100 group-hover:opacity-100"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </Tooltip>
                </div>
              );
            })}
            <button
              type="button"
              onClick={() => setShowDocumentSelectionModal(true)}
              className="-mx-1.5 mt-0.5 inline-flex items-center gap-1 rounded-[6px] px-1.5 py-1 text-[#4F5663] hover:bg-[var(--fp-hover)] hover:text-[#10141B]"
            >
              {localDocumentIds.length === 0 ? (
                <Paperclip className="h-3.5 w-3.5" />
              ) : (
                <Plus className="h-3.5 w-3.5" />
              )}
              <span>Attach</span>
            </button>
          </div>
        </div>
      )}

      {showDocumentSelectionModal && (
        <V2FileSelectionModal
          isOpen={showDocumentSelectionModal}
          onClose={() => setShowDocumentSelectionModal(false)}
          onConfirm={(fileIds) => {
            handleDocumentsSelected(fileIds);
            setShowDocumentSelectionModal(false);
          }}
          excludeBlobKinds={[
            "finpilot-interaction",
            "email",
            "unsupported",
            "unreadable",
          ]}
        />
      )}
    </div>
  );
};

const ViewerImpl = ({
  onEscape,
  onDownload,
}: {
  onEscape?: () => void;
  /** The pane header's Download, for the viewer's own keyboard save. */
  onDownload?: () => void;
}) => {
  const fileViewerData = useSelector(
    (state: RootState) => state.fileViewerData
  );
  const {
    fileId,
    fileType: fileTypeArg,
    fileName,
    previewUrl,
    interactionId,
    interactionError,
  } = fileViewerData;

  const interactionQuery = useViewerInteraction(
    fileId,
    interactionId,
    !interactionError &&
      (Boolean(interactionId) || fileTypeArg !== "finpilot-interaction")
  );
  const interaction =
    interactionQuery.data &&
    (interactionId || interactionQuery.data.document_id === fileId)
      ? interactionQuery.data
      : null;
  const currentFileId = interactionId
    ? (interaction?.document_id ?? null)
    : fileId;

  if (!fileId && !previewUrl && !interactionId) {
    return <></>;
  }

  const currentType =
    interactionViewerKind(interaction?.document_blob_kind) || fileTypeArg;
  const exactNote =
    Boolean(interactionId) && currentType === "finpilot-interaction";
  // An unreadable file has nothing to preview (#11277); its arm says why.
  const shouldLoad =
    !exactNote &&
    currentType !== "unreadable" &&
    (currentType !== "unsupported" ||
      isPreviewableUnsupportedExtension(fileName));

  const {
    fileType,
    response,
    fileUrl,
    error,
    isLoading,
    unrecognizedKind,
    isLocked,
  } = useFileLoader(currentFileId, currentType, shouldLoad, previewUrl);

  const renderViewer = () => {
    if (interactionError || (interactionId && interactionQuery.error))
      return (
        <p role="alert" className="p-4">
          Unable to load this interaction
        </p>
      );
    if (interactionId && !interaction) return <CircularProgress size={32} />;
    // The states that are not a kind at all are settled FIRST, in the same
    // order as the RMS surface (RMS/Components/DocumentViewer.tsx). Dispatching
    // ahead of these reads "no bytes yet" as "no viewer for this", which turns
    // the `unsupported` arm's terminal message into a loading state and hides
    // the load-failure box entirely.
    if (error) {
      return (
        <div className="p-6">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="text-red-800 font-semibold">
              Unable to load file
            </div>
          </div>
        </div>
      );
    }

    // Before the dispatch, not after: `useFileLoader` falls back to the
    // caller-supplied kind when the wire declares one it cannot name, and that
    // fallback is frequently a real registry key. Checking afterwards would
    // render that viewer and silently swallow the drift we just detected.
    if (unrecognizedKind) {
      return <UnrecognizedKind kind={unrecognizedKind} />;
    }

    if (isLoading || !fileType) {
      return (
        <div className="h-full w-full flex items-center justify-center">
          <CircularProgress size={32} />
        </div>
      );
    }

    // A still-locked document gets the viewer's own password panel rather
    // than its kind's renderer: pdf.js would show its own dialog (and store
    // nothing), the sheet grid would stop at a message (#11856).
    if (isLocked && currentFileId) {
      return <LockedDocumentPanel fileId={currentFileId} />;
    }

    // One shared dispatch table (#11712). `fileType` has narrowed to ViewerKind
    // here, so the lookup needs no cast and no membership probe -- both would be
    // the softening this registry exists to prevent.
    if (fileType !== "iodl-source") {
      return VIEWER_REGISTRY[fileType]({
        response,
        fileUrl,
        fileId: currentFileId ?? "",
        interactionId: interactionId ?? undefined,
        onEscape,
        sheetName: fileViewerData?.sheetName || undefined,
        onDownload,
        unreadableReason: fileViewerData?.unreadableReason,
        fileName: fileName ?? undefined,
      });
    }

    return <UnrecognizedKind kind={String(fileType)} />;
  };

  return (
    <>
      {interaction && fileType !== "finpilot-interaction" && (
        <InteractionBanner key={interaction.id} interaction={interaction} />
      )}
      <CitationLinkInterceptor>{renderViewer()}</CitationLinkInterceptor>
    </>
  );
};

export default ViewerImpl;
